import { useQuery } from '@tanstack/react-query';
import { supaFetch } from '../lib/supabase.js';
import { queryKeys } from '../lib/queryKeys.js';

// ─── CUSTOMER → CATALOGUE TOKEN INDEX ─────────────────────────────────────────
// The `customers` CRM table is phone-keyed and has no FK to
// retailer_applications (the accounts that own catalogue tokens), so a CRM row
// can't link to its catalogue directly. Rather than block "View as customer"
// on the full CRM/accounts reconciliation, this resolves the link by matching
// email first, then phone.
//
// One query for the whole screen — not one per row — so opening Customers
// costs a single extra request regardless of list size.

const normEmail = (e) => (e || '').trim().toLowerCase() || null;

// Egyptian numbers arrive as 01…, +201…, 00201… — compare on the last 9
// digits, which is the stable part across all of those forms.
const normPhone = (p) => {
  const d = (p || '').replace(/\D/g, '');
  return d.length >= 9 ? d.slice(-9) : null;
};

const usable = (t) =>
  t.active && (!t.expires_at || new Date(t.expires_at).getTime() > Date.now());

export function useCustomerTokenIndex() {
  const { data } = useQuery({
    queryKey: queryKeys.admin.customerTokenIndex(),
    queryFn: () => supaFetch('GET', 'retailer_applications', null,
      '?status=eq.approved&select=id,email,phone,company_name,catalogue_tokens(token,active,expires_at,created_at)'),
    select: (result) => {
      const byId = new Map();
      const byEmail = new Map();
      const byPhone = new Map();
      for (const app of result.data ?? []) {
        const tokens = (Array.isArray(app.catalogue_tokens) ? app.catalogue_tokens : [])
          .filter(usable)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        if (!tokens.length) continue;
        const entry = { token: tokens[0].token, accountId: app.id, company: app.company_name };
        byId.set(app.id, entry);
        const e = normEmail(app.email);
        const p = normPhone(app.phone);
        if (e && !byEmail.has(e)) byEmail.set(e, entry);
        if (p && !byPhone.has(p)) byPhone.set(p, entry);
      }
      return { byId, byEmail, byPhone };
    },
    staleTime: 5 * 60 * 1000,
  });

  // Returns { token, accountId, company } for a CRM row, or null when that
  // customer has no approved account with a usable catalogue link.
  // customers.retailer_application_id (added 2026-07-31) is authoritative; the
  // email/phone match below only covers rows written before it existed or by a
  // path that could not resolve the account.
  const findAccount = (customer) => {
    if (!data || !customer) return null;
    if (customer.retailer_application_id && data.byId.has(customer.retailer_application_id)) {
      return data.byId.get(customer.retailer_application_id);
    }
    const e = normEmail(customer.email);
    if (e && data.byEmail.has(e)) return data.byEmail.get(e);
    for (const raw of [customer.phone, customer.whatsapp]) {
      const p = normPhone(raw);
      if (p && data.byPhone.has(p)) return data.byPhone.get(p);
    }
    return null;
  };

  return { findAccount, ready: !!data };
}
